import React from 'react';
import './Service.css'; // Import the specific CSS for this page

// Placeholder Image (Replace with relevant images per section later)
import servicePlaceholder from './heath.webp'; // Assuming this path is correct
import two from "../Assets/2.png"
import three from "../Assets/3.png"
import four from "../Assets/4.png"
import five from "../Assets/5.png"
import six from "../Assets/6.png"
import seven from "../Assets/7.png"
import eight from "../Assets/9.png"

// Reusable Intersection Observer Hook (Keep as is)
const useVisibility = (options) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const elementRef = React.useRef(null);
    const observerRef = React.useRef(null);

    React.useEffect(() => {
        const currentElement = elementRef.current;
        if (!currentElement) return;
        observerRef.current = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Unobserve after first view for performance
                    observerRef.current.unobserve(currentElement);
                }
                // No 'else' block means animation triggers once
            });
        }, options);
        observerRef.current.observe(currentElement);
        return () => { if (observerRef.current && currentElement) observerRef.current.unobserve(currentElement); };
    }, [options]); // Re-run effect if options change (though they don't here)
    return [elementRef, isVisible];
};


// Placeholder Icon (Can be enhanced later)
const ServiceIcon = ({ children }) => <span className="service-page-feature-icon">{children}</span>;

function ServicePage() {
    // Refs for scroll animations for all sections
    const [headerRef, isHeaderVisible] = useVisibility({ threshold: 0.3 });
    const [manpowerRef, isManpowerVisible] = useVisibility({ threshold: 0.2 });
    const [amcRef, isAmcVisible] = useVisibility({ threshold: 0.2 });
    const [infraRef, isInfraVisible] = useVisibility({ threshold: 0.2 });
    const [healthcareRef, isHealthcareVisible] = useVisibility({ threshold: 0.2 });
    const [physioRef, isPhysioVisible] = useVisibility({ threshold: 0.2 });
    const [pharmacyRef, isPharmacyVisible] = useVisibility({ threshold: 0.2 });
    const [mentalHealthRef, isMentalHealthVisible] = useVisibility({ threshold: 0.2 });
    const [whyChooseUsRef, isWhyChooseUsVisible] = useVisibility({ threshold: 0.2 });
    const [ctaRef, isCtaVisible] = useVisibility({ threshold: 0.3 });

    return (
        <div className="service-page-container">

            {/* ==== Page Header ==== */}
            <header ref={headerRef} className={`service-page-header ${isHeaderVisible ? 'is-visible' : ''}`}>
                <div className="service-page-header-bg-shapes">
                    <div className="service-page-shape service-page-shape-1"></div>
                    <div className="service-page-shape service-page-shape-2"></div>
                </div>
                <div className="service-page-header-content">
                    <h1 className="service-page-main-title">Our Premier Services at Cliniqor Healthcare</h1>
                    <p className="service-page-subtitle">
                        Dedicated to transforming healthcare and organizational operations through unparalleled services merging innovation with expertise. Our offerings streamline operations, enhance patient care, and optimize infrastructure.
                    </p>
                </div>
            </header>

            {/* ==== Service Section 1: Manpower ==== */}
            <section ref={manpowerRef} className={`service-page-section service-manpower ${isManpowerVisible ? 'is-visible' : ''}`}>
                <div className="service-page-grid service-layout-a">
                    <div className="service-page-image-column">
                        <div className="service-page-image-wrapper service-page-image-reveal">
                            <img src={two} alt="Diverse team of skilled healthcare and administrative professionals" className="service-page-image" />
                            <div className="service-page-image-overlay-gradient"></div>
                        </div>
                    </div>
                    <div className="service-page-content-column service-page-text-reveal">
                        <span className="service-page-tag">Talent & Staffing</span>
                        <h2 className="service-page-title">Comprehensive Manpower Solutions & Staff Recruitment</h2>
                        <p className="service-page-description">
                            Tailored to meet the staffing demands of healthcare and educational sectors, providing your organization with highly skilled professionals. Through a rigorous recruitment process, we connect you with top-tier talent across all domains — from doctors and nurses to administrative personnel.
                        </p>
                        <ul className="service-page-features-list">
                            <li><ServiceIcon>✓</ServiceIcon> Customized Talent Acquisition Process</li>
                            <li><ServiceIcon>✓</ServiceIcon> Access to Highly Skilled Professionals</li>
                            <li><ServiceIcon>✓</ServiceIcon> Onboarding & Training Support</li>
                            <li><ServiceIcon>✓</ServiceIcon> Clinical, Operational & Support Staff</li>
                        </ul>
                         <a href="/contact" className="service-page-cta-link">Find Your Ideal Staff →</a>
                    </div>
                </div>
            </section>

             {/* Section Divider */}
             <div className="service-page-divider sp-divider-wave"></div>

            {/* ==== Service Section 2: AMC ==== */}
             <section ref={amcRef} className={`service-page-section service-amc ${isAmcVisible ? 'is-visible' : ''}`}>
                <div className="service-page-grid service-layout-b">
                     <div className="service-page-content-column service-page-text-reveal">
                         <span className="service-page-tag">Operational Reliability</span>
                        <h2 className="service-page-title">Premium Annual Maintenance Contracts (AMC)</h2>
                        <p className="service-page-description">
                            Ensure the longevity and optimal functionality of your medical equipment, infrastructure, and facilities. We offer end-to-end support, from routine checks to emergency repairs, guaranteeing uninterrupted service with proactive diagnostics and preventive care to minimize downtime and costly repairs.
                        </p>
                        <ul className="service-page-features-list">
                            <li><ServiceIcon>✓</ServiceIcon> Comprehensive Equipment & Facility Coverage</li>
                            <li><ServiceIcon>✓</ServiceIcon> Proactive Maintenance & Diagnostics</li>
                            <li><ServiceIcon>✓</ServiceIcon> Minimized Operational Downtime</li>
                            <li><ServiceIcon>✓</ServiceIcon> Emergency Repair Support</li>
                        </ul>
                         <a href="/contact" className="service-page-cta-link">Secure Your Assets →</a>
                    </div>
                    <div className="service-page-image-column">
                        <div className="service-page-image-wrapper service-page-image-reveal">
                            <img src={three} alt="Technician performing maintenance on medical equipment" className="service-page-image" />
                             <div className="service-page-image-overlay-pattern"></div>
                        </div>
                    </div>
                </div>
            </section>

             {/* Section Divider */}
             <div className="service-page-divider sp-divider-angle"></div>

            {/* ==== Service Section 3: Infrastructure ==== */}
            <section ref={infraRef} className={`service-page-section service-infra ${isInfraVisible ? 'is-visible' : ''}`}>
                 <div className="service-page-grid service-layout-a">
                     <div className="service-page-image-column">
                        <div className="service-page-image-wrapper service-page-image-reveal">
                            <img src={four} alt="Architectural blueprint of a modern healthcare facility" className="service-page-image" />
                             <div className="service-page-image-overlay-glow"></div>
                        </div>
                    </div>
                    <div className="service-page-content-column service-page-text-reveal">
                        <span className="service-page-tag">Development & Design</span>
                        <h2 className="service-page-title">Expertise in Infrastructure Consultancy</h2>
                        <p className="service-page-description">
                            Crafted to build, renovate, and optimize spaces for healthcare, colleges, and hostels, fostering efficiency, safety, and growth. We provide tailored guidance and management from design to execution, ensuring state-of-the-art facilities that meet all requirements.
                        </p>
                         <ul className="service-page-features-list">
                            <li><ServiceIcon>✓</ServiceIcon> Hospital & Healthcare Facility Design</li>
                            <li><ServiceIcon>✓</ServiceIcon> Educational Campus & Hostel Development</li>
                            <li><ServiceIcon>✓</ServiceIcon> End-to-End Project Management</li>
                            <li><ServiceIcon>✓</ServiceIcon> Regulatory Compliance & Space Planning</li>
                        </ul>
                        <a href="/contact" className="service-page-cta-link">Build Your Vision →</a>
                    </div>
                </div>
            </section>

            {/* Section Divider */}
            <div className="service-page-divider sp-divider-wave"></div>

            {/* ==== Service Section 4: Healthcare Services ==== */}
            <section ref={healthcareRef} className={`service-page-section service-healthcare ${isHealthcareVisible ? 'is-visible' : ''}`}>
                <div className="service-page-grid service-layout-b">
                    <div className="service-page-content-column service-page-text-reveal">
                        <span className="service-page-tag">Patient Care Excellence</span>
                        <h2 className="service-page-title">World-Class Healthcare Services</h2>
                        <p className="service-page-description">
                            Providing comprehensive, accessible, and top-quality healthcare. Our multidisciplinary approach covers preventive care, specialized treatments, diagnostics, and emergency services, ensuring holistic patient well-being.
                        </p>
                        <ul className="service-page-features-list">
                            <li><ServiceIcon>✓</ServiceIcon> General Medicine & Specialized Care (Cardiology, Ortho, etc.)</li>
                            <li><ServiceIcon>✓</ServiceIcon> Preventive Health Checkups & Advanced Diagnostics</li>
                            <li><ServiceIcon>✓</ServiceIcon> 24/7 Emergency Response & Critical Care</li>
                            <li><ServiceIcon>✓</ServiceIcon> Comprehensive Maternity & Neonatal Care</li>
                        </ul>
                        <a href="/contact" className="service-page-cta-link">Explore Care Options →</a>
                    </div>
                    <div className="service-page-image-column">
                        <div className="service-page-image-wrapper service-page-image-reveal">
                            <img src={five} alt="Doctor consulting with a patient in a modern clinic" className="service-page-image" />
                            <div className="service-page-image-overlay-pattern"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section Divider */}
            <div className="service-page-divider sp-divider-angle"></div>

            {/* ==== Service Section 5: Physiotherapy ==== */}
            <section ref={physioRef} className={`service-page-section service-physio ${isPhysioVisible ? 'is-visible' : ''}`}>
                 <div className="service-page-grid service-layout-a">
                     <div className="service-page-image-column">
                        <div className="service-page-image-wrapper service-page-image-reveal">
                            <img src={six} alt="Physiotherapist assisting a patient with rehabilitation exercises" className="service-page-image" />
                             <div className="service-page-image-overlay-glow"></div>
                        </div>
                    </div>
                    <div className="service-page-content-column service-page-text-reveal">
                        <span className="service-page-tag">The Cliniqor Difference</span>
                        <h2 className="service-page-title">Why Partner with Cliniqor Healthcare?</h2>
                        <p className="service-page-description">
                            Understanding the importance of rehabilitation, our services restore function, alleviate pain, and support full recovery using personalized plans and cutting-edge therapies guided by expert care teams.
                        </p>
                         <ul className="service-page-features-list">
                            <li><ServiceIcon>✓</ServiceIcon> Personalized Recovery Plans (Post-Surgery, Injury, Chronic)</li>
                            <li><ServiceIcon>✓</ServiceIcon> Cutting-Edge Physiotherapy Techniques</li>
                            <li><ServiceIcon>✓</ServiceIcon> Certified Specialists & Dedicated Support</li>
                            <li><ServiceIcon>✓</ServiceIcon> Focus on Functional Restoration & Quality of Life</li>
                        </ul>
                        <a href="/contact" className="service-page-cta-link">Start Your Recovery →</a>
                    </div>
                </div>
            </section>

            {/* Section Divider */}
            <div className="service-page-divider sp-divider-wave"></div>

            {/* ==== Service Section 6: Pharmacy ==== */}
            <section ref={pharmacyRef} className={`service-page-section service-pharmacy ${isPharmacyVisible ? 'is-visible' : ''}`}>
                <div className="service-page-grid service-layout-b">
                    <div className="service-page-content-column service-page-text-reveal">
                        <span className="service-page-tag">Medication Access</span>
                        <h2 className="service-page-title">In-House Pharmacy Services</h2>
                        <p className="service-page-description">
                            Providing seamless access to medications and healthcare supplies. Stocked with the latest pharmaceuticals and overseen by professional pharmacists, ensuring patients conveniently get what they need for their treatment with expert guidance.
                        </p>
                        <ul className="service-page-features-list">
                            <li><ServiceIcon>✓</ServiceIcon> Comprehensive Prescription & OTC Options</li>
                            <li><ServiceIcon>✓</ServiceIcon> Convenient On-Site Access</li>
                            <li><ServiceIcon>✓</ServiceIcon> Professional Pharmacist Guidance</li>
                            <li><ServiceIcon>✓</ServiceIcon> Focus on Patient Safety & Well-being</li>
                        </ul>
                        <a href="/contact" className="service-page-cta-link">Visit Our Pharmacy →</a>
                    </div>
                    <div className="service-page-image-column">
                        <div className="service-page-image-wrapper service-page-image-reveal">
                            <img src={seven} alt="Well-stocked modern pharmacy counter" className="service-page-image" />
                            <div className="service-page-image-overlay-pattern"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section Divider */}
            <div className="service-page-divider sp-divider-angle"></div>

            {/* ==== Service Section 7: Mental Health ==== */}
            <section ref={mentalHealthRef} className={`service-page-section service-mental-health ${isMentalHealthVisible ? 'is-visible' : ''}`}>
                 <div className="service-page-grid service-layout-a">
                     <div className="service-page-image-column">
                        <div className="service-page-image-wrapper service-page-image-reveal">
                            <img src={eight} alt="Therapist conducting a counseling session in a calm setting" className="service-page-image" />
                             <div className="service-page-image-overlay-glow"></div>
                        </div>
                    </div>
                    <div className="service-page-content-column service-page-text-reveal">
                        <span className="service-page-tag">Well-being Support</span>
                        <h2 className="service-page-title">Mental Health & Counseling Services</h2>
                        <p className="service-page-description">
                            Supporting emotional and psychological well-being with compassionate counseling and therapy. Our team addresses a wide range of conditions in a confidential, supportive environment, focusing on tailored plans for long-term growth.
                        </p>
                         <ul className="service-page-features-list">
                            <li><ServiceIcon>✓</ServiceIcon> Care for Anxiety, Depression, Stress, Trauma & More</li>
                            <li><ServiceIcon>✓</ServiceIcon> Confidential & Non-Judgmental Environment</li>
                            <li><ServiceIcon>✓</ServiceIcon> Tailored Treatment Plans by Experts</li>
                            <li><ServiceIcon>✓</ServiceIcon> Focus on Long-Term Well-being & Personal Growth</li>
                        </ul>
                        <a href="/contact" className="service-page-cta-link">Find Support →</a>
                    </div>
                </div>
            </section>

             {/* Section Divider */}
             <div className="service-page-divider sp-divider-wave"></div>

           


             {/* ==== Final CTA Section ==== */}
             <section ref={ctaRef} className={`service-page-cta-section ${isCtaVisible ? 'is-visible' : ''}`}>
                 <div className="service-page-cta-content">
                     <h3 className="service-page-cta-title">Ready to Elevate Your Organization?</h3>
                     <p className="service-page-cta-text">
                         Let Cliniqor Healthcare be your trusted partner. Contact us today to learn how our world-class services can support your goals.
                     </p>
                     <a href="/contact" className="service-page-cta-button">
                         Get In Touch
                         <span className="service-page-cta-button-shine"></span>
                     </a>
                 </div>
                 <div className="service-page-cta-bg-elements">
                    <div className="service-page-cta-blob cta-blob-1"></div>
                    <div className="service-page-cta-blob cta-blob-2"></div>
                 </div>
             </section>

        </div> // End service-page-container
    );
}

export default ServicePage;